import { apiClient, toCamelCase, toSnakeCase } from "./client";
import type { UsageInfo, QuotaUpgradeRequest, ForgePreferences } from "@/types/user";

export const profileApi = {
  /** Estado de cuota del usuario en el mes en curso (alimenta widget + carteles). */
  getMyUsage: async (): Promise<UsageInfo> => {
    const res = await apiClient.get("/profile/usage");
    return toCamelCase(res.data) as UsageInfo;
  },

  /** Solicita una ampliación de cuota (queda pendiente de aprobación admin). */
  requestQuotaUpgrade: async (
    requestedAmountUsd: number,
    reason?: string
  ): Promise<{ id: number; status: string; requestedAmountUsd: number }> => {
    const res = await apiClient.post("/profile/request-quota-upgrade", {
      requested_amount_usd: requestedAmountUsd,
      reason,
    });
    return toCamelCase(res.data) as {
      id: number;
      status: string;
      requestedAmountUsd: number;
    };
  },

  /** Preferencias personales de la Forja (modificadores por defecto). */
  getForgePreferences: async (): Promise<ForgePreferences> => {
    const res = await apiClient.get("/profile/forge-preferences");
    const data = toCamelCase(res.data) as { forgePreferences: ForgePreferences };
    return data.forgePreferences || {};
  },

  updateForgePreferences: async (
    payload: Partial<ForgePreferences>
  ): Promise<ForgePreferences> => {
    const body = toSnakeCase(payload as unknown as Record<string, unknown>);
    const res = await apiClient.put("/profile/forge-preferences", body);
    const data = toCamelCase(res.data) as { forgePreferences: ForgePreferences };
    return data.forgePreferences || {};
  },
};

export interface Notification {
  id: number;
  type: string;
  title: string;
  body?: string | null;
  level: string;
  read: boolean;
  createdAt?: string | null;
}

export const notificationsApi = {
  /** Notificaciones del usuario (más recientes primero). */
  list: async (onlyUnread = false, limit = 50): Promise<Notification[]> => {
    const res = await apiClient.get("/profile/notifications", {
      params: { only_unread: onlyUnread, limit },
    });
    return toCamelCase(res.data) as Notification[];
  },

  /** Nº de notificaciones sin leer (para el badge de la campana). */
  unreadCount: async (): Promise<number> => {
    const res = await apiClient.get("/profile/notifications/unread-count");
    const data = toCamelCase(res.data) as { unread: number };
    return data.unread ?? 0;
  },

  markRead: async (id: number): Promise<void> => {
    await apiClient.post(`/profile/notifications/${id}/read`);
  },

  markAllRead: async (): Promise<void> => {
    await apiClient.post("/profile/notifications/read-all");
  },
};

export const quotaAdminApi = {
  /** Lista solicitudes de ampliación (por defecto pendientes). Solo superadmin. */
  listRequests: async (
    statusFilter: "pending" | "approved" | "rejected" | "all" = "pending"
  ): Promise<QuotaUpgradeRequest[]> => {
    const res = await apiClient.get("/admin/quota-upgrade-requests", {
      params: { status_filter: statusFilter },
    });
    const data = toCamelCase(res.data) as { items: QuotaUpgradeRequest[] };
    return data.items || [];
  },

  approve: async (id: number): Promise<void> => {
    await apiClient.post(`/admin/quota-upgrade-requests/${id}/approve`);
  },

  reject: async (id: number): Promise<void> => {
    await apiClient.post(`/admin/quota-upgrade-requests/${id}/reject`);
  },
};
